import React, { ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import debounce from 'lodash/debounce';
import isFinite from 'lodash/isFinite';
import * as types from '../../store/constants';
import { StButton, StDisabledButton, StGrowingInput, StNumberSpinner } from './NumberSpinner.styled';
import { usePreviousProps } from '../../hooks/usePreviousProps';

type Product = {
    readonly netValue?: number;
};
interface Props {
    value?: number;
    min?: number;
    max?: number;
    StGrowingInput?: typeof StGrowingInput;
    onChange: (type: string, value: number) => void;
    className?: string;
    disabled?: boolean;
    product: Product;
}

function NumberSpinner({ onChange, min, max, className, disabled, product, value }: Props) {
    const [inputValue, setInputValue] = useState(0);
    let inputRef = useRef() as React.MutableRefObject<HTMLInputElement>;
    const onDecreaseDebounced = debounce(onDecrease, 500, {
        leading: true,
        trailing: false,
        maxWait: 500
    });
    const onIncreaseDebounced = debounce(onIncrease, 500, {
        leading: true,
        trailing: false,
        maxWait: 500
    });

    function onDecrease() {
        setInputValue(currentInputValue => currentInputValue - 1);
        onChange(types.ACTION_DECREASE, 1);
    }

    function onIncrease() {
        setInputValue(currentInputValue => currentInputValue + 1);
        onChange(types.ACTION_INCREASE, 1);
    }

    const sanitizeInput = useCallback(
        inputValue => {
            let sanitizedInput = parseInt(inputValue);
            if (!isFinite(sanitizedInput)) {
                return min;
            }
            if (min !== null && min !== undefined && sanitizedInput < min) {
                sanitizedInput = min;
            }
            if (max !== null && max !== undefined && sanitizedInput > max) {
                sanitizedInput = max;
            }
            return sanitizedInput;
        },
        [max, min]
    );

    const onInputValueChange = useCallback(
        inputValue => {
            const sanitizedInput = sanitizeInput(inputValue);
            setInputValue(sanitizedInput!);
        },
        [sanitizeInput]
    );

    const onEnterKey = useCallback(() => {
        onChange(types.ACTION_SET, inputValue);
        inputRef?.current?.blur();
    }, [inputValue, onChange]);

    const prevValue = usePreviousProps(value);

    useEffect(() => {
        if (prevValue !== value) {
            onInputValueChange(value);
        }
    }, [onInputValueChange, prevValue, value]);

    if (product && !product.netValue && product.netValue !== 0) {
        // this is a withdrawn/unavailable product!
        return null;
    }

    let decreaseButton: ReactNode;
    if (!disabled && inputValue > 1) {
        decreaseButton = <StButton onClick={onDecreaseDebounced}>-</StButton>;
    } else {
        decreaseButton = <StDisabledButton>-</StDisabledButton>;
    }
    let increaseButton: ReactNode;
    if (!disabled) {
        increaseButton = <StButton onClick={onIncreaseDebounced}>+</StButton>;
    } else {
        increaseButton = <StDisabledButton>+</StDisabledButton>;
    }

    return (
        <StNumberSpinner className={className} disabled={disabled}>
            {decreaseButton}
            <StGrowingInput
                ref={inputRef}
                value={inputValue.toString()}
                minWidthInPx={25}
                maxWidthInPx={50}
                maxLength={4}
                onEnterKey={onEnterKey}
                onChange={onInputValueChange}
            />
            {increaseButton}
        </StNumberSpinner>
    );
}

export default NumberSpinner;
